import { getPorts } from "@velizhanin-dev/bot-scenario"
import type { DraftNode, NodePosition, ScenarioDraft } from "../api/types"
import { attachmentLabel } from "./attachment-label"
import { autoLayout, estimateNodeHeight, NODE_WIDTH } from "./auto-layout"

const HORIZONTAL_GAP = 100
const VERTICAL_GAP = 40

/** Первый выход блока, от которого ещё не идёт связь; null: все заняты. */
export function findFreePort(draft: ScenarioDraft, node: DraftNode): string | null {
	const used = new Set(
		draft.edges.filter((edge) => edge.sourceNodeId === node.id).map((edge) => edge.sourcePort),
	)
	return getPorts(node).find((port) => !used.has(port.id))?.id ?? null
}

/**
 * Куда поставить новый блок. Рядом с выбранным: справа, ниже уже стоящих там
 * блоков; без выбранного: под самым нижним блоком потока.
 */
export function placeNewNode(
	draft: ScenarioDraft,
	flowId: string,
	anchor: DraftNode | null,
): { x: number; y: number } {
	const flowNodes = draft.nodes.filter((node) => node.flowId === flowId)
	const bottomOf = (node: DraftNode) =>
		node.positionY +
		estimateNodeHeight(
			getPorts(node).length,
			node.type === "start",
			attachmentLabel(node.config) !== null,
		)

	if (anchor) {
		const x = anchor.positionX + NODE_WIDTH + HORIZONTAL_GAP
		const column = flowNodes.filter(
			(node) => node.positionX < x + NODE_WIDTH && node.positionX + NODE_WIDTH > x,
		)
		const y =
			column.length > 0 ? Math.max(...column.map(bottomOf)) + VERTICAL_GAP : anchor.positionY
		return { x: Math.round(x), y: Math.round(Math.max(y, anchor.positionY)) }
	}

	if (flowNodes.length === 0) return { x: 0, y: 0 }
	const left = Math.min(...flowNodes.map((node) => node.positionX))
	return { x: Math.round(left), y: Math.round(Math.max(...flowNodes.map(bottomOf)) + VERTICAL_GAP) }
}

/** «Упорядочить»: новые координаты только тех блоков потока, что сдвинулись. */
export function layoutFlow(draft: ScenarioDraft, flowId: string): NodePosition[] {
	const flowNodes = draft.nodes.filter((node) => node.flowId === flowId)
	const positions = autoLayout(
		flowNodes.map((node) => ({
			id: node.id,
			portCount: getPorts(node).length,
			isStart: node.type === "start",
			hasAttachment: attachmentLabel(node.config) !== null,
		})),
		draft.edges.map((edge) => ({ source: edge.sourceNodeId, target: edge.targetNodeId })),
	)
	return flowNodes.flatMap((node) => {
		const next = positions.get(node.id)
		if (!next || (next.x === node.positionX && next.y === node.positionY)) return []
		return [{ nodeId: node.id, x: next.x, y: next.y }]
	})
}

/** Число ошибок проверки по потокам: для бейджей на вкладках потоков. */
export function countErrorsByFlow(
	draft: ScenarioDraft,
	issues: { severity: string; nodeId?: string; flowId?: string }[],
): Map<string, number> {
	const flowOfNode = new Map(draft.nodes.map((node) => [node.id, node.flowId]))
	const counts = new Map<string, number>()
	for (const issue of issues) {
		if (issue.severity !== "error") continue
		const flowId = (issue.nodeId && flowOfNode.get(issue.nodeId)) || issue.flowId
		if (flowId) counts.set(flowId, (counts.get(flowId) ?? 0) + 1)
	}
	return counts
}
